/**
 * Data Sync API Client
 * MariaDB (RDS) と Cognito のユーザーデータ同期 API
 */
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// ==========================================
// Type Definitions
// ==========================================

export interface UserSyncInfo {
  id: string;
  email: string;
  username: string;
  name: string;
  permissionLevel: string;
}

export interface CognitoUserInfo {
  username: string;
  email: string;
  name?: string;
  permissionLevel?: string;
}

export interface AttributeMismatch {
  attribute: string;
  rdsValue: string | null;
  cognitoValue: string | null;
}

export interface AttributeMismatchInfo {
  userId: string;
  email: string;
  username: string;
  mismatches: AttributeMismatch[];
}

export interface SyncPreviewResult {
  checkTime: string;
  usersOnlyInRds: UserSyncInfo[];
  usersOnlyInCognito: CognitoUserInfo[];
  usersWithMismatch: AttributeMismatchInfo[];
  summary: {
    totalRdsUsers: number;
    totalCognitoUsers: number;
    onlyInRdsCount: number;
    onlyInCognitoCount: number;
    mismatchCount: number;
  };
}

export interface SyncOptions {
  createMissingCognitoUsers: boolean;
  syncAttributes: boolean;
  dryRun: boolean;
}

export interface SyncUserResult {
  userId?: string;
  email: string;
  username: string;
  action: 'created' | 'updated' | 'skipped' | 'failed';
  success: boolean;
  error?: string;
}

export interface SyncError {
  userId?: string;
  email: string;
  operation: string;
  message: string;
}

export interface SyncExecutionResult {
  executionTime: string;
  dryRun: boolean;
  usersCreated: {
    successCount: number;
    failedCount: number;
    details: SyncUserResult[];
  };
  attributesUpdated: {
    successCount: number;
    failedCount: number;
    details: SyncUserResult[];
  };
  errors: SyncError[];
}

// ==========================================
// API Functions
// ==========================================

/**
 * Get authorization header
 */
async function getAuthHeader(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return token || '';
}

/**
 * Get sync preview - detect inconsistencies
 */
export async function getPreview(): Promise<SyncPreviewResult> {
  const token = await getAuthHeader();
  const response = await axios.get<SyncPreviewResult>(
    `${API_BASE_URL}/admin/sync/preview`,
    {
      headers: { Authorization: token },
    }
  );
  return response.data;
}

/**
 * Execute sync operation
 */
export async function executeSync(options: SyncOptions): Promise<SyncExecutionResult> {
  const token = await getAuthHeader();
  const response = await axios.post<SyncExecutionResult>(
    `${API_BASE_URL}/admin/sync/execute`,
    options,
    {
      headers: { Authorization: token },
    }
  );
  return response.data;
}

/**
 * Sync single user
 */
export async function syncSingleUser(userId: string): Promise<SyncUserResult> {
  const token = await getAuthHeader();
  const response = await axios.post<SyncUserResult>(
    `${API_BASE_URL}/admin/sync/user/${userId}`,
    {},
    {
      headers: { Authorization: token },
    }
  );
  return response.data;
}

export const dataSyncApi = {
  getPreview,
  executeSync,
  syncSingleUser,
};
